// Single source-of-truth for the BE's questionType discriminator.
// Backed by GET /api/consumer/assessment/question-types (no auth).
// Fetched once per page lifetime, cached in module scope.
//
// We pin against `EXPECTED_CONTRACT_VERSION` — when the BE bumps the
// major version (breaking change), this module logs a clear console
// error and the assessment flow shows a soft "we need to refresh"
// banner. Minor BE changes (new questionType, new settings) don't
// trip the gate.
//
// FE consumers:
//   import { getContract, isKnownType, isDeprecatedType } from
//     "@/lib/questionTypesContract";
//   await getContract();           // resolves once, then memoized
//   isKnownType("medication_list") // true after contract is loaded
//   isDeprecatedType("drug_autocomplete") // true
import { apiGet } from "@/lib/api";

export const EXPECTED_CONTRACT_VERSION = "1.0";

// Hardcoded fallback used when the BE endpoint hasn't responded yet
// (or is unreachable). Mirrors the 12 types we know about so the
// dispatcher can still classify even without a successful fetch.
// Update this list at the same time as BE bumps the contract — the
// runtime fetch will then verify the pin.
export const KNOWN_TYPES = new Set([
  "text",
  "long_text",
  "date",
  "scale",
  "yes_no",
  "single_choice",
  "multi_choice",
  "managed_multi_choice",
  "medication_list",
  "photo_upload",
  "drug_autocomplete", // deprecated — kept so legacy sessions still render
  "body_diagram",
]);

export const DEPRECATED_TYPES = new Set(["drug_autocomplete"]);

const MULTI_SELECT_TYPES_DEFAULT = new Set([
  "multi_choice",
  "managed_multi_choice",
  "medication_list",
  "photo_upload",
  "body_diagram",
]);

let _cache = null;
let _inFlight = null;
let _versionMismatch = false;

export async function getContract() {
  if (_cache) return _cache;
  if (_inFlight) return _inFlight;
  _inFlight = (async () => {
    try {
      const res = await apiGet("/consumer/assessment/question-types");
      if (res?.contractVersion && res.contractVersion !== EXPECTED_CONTRACT_VERSION) {
        _versionMismatch = true;
        // eslint-disable-next-line no-console
        console.error(
          `[VOYA9] Question-types contract version mismatch — BE shipped ${res.contractVersion}, FE expects ${EXPECTED_CONTRACT_VERSION}. The assessment surface may behave unexpectedly until the FE is redeployed.`
        );
      }
      _cache = res || null;
      return _cache;
    } catch {
      _cache = null;
      return null;
    } finally {
      _inFlight = null;
    }
  })();
  return _inFlight;
}

export function hasContractVersionMismatch() {
  return _versionMismatch;
}

// Synchronous checks — safe to call before getContract() resolves
// because they fall back to the hardcoded sets above.
export function isKnownType(type) {
  const t = String(type || "").toLowerCase();
  if (_cache?.questionTypes) {
    return _cache.questionTypes.some((q) => (q.questionType || "").toLowerCase() === t);
  }
  return KNOWN_TYPES.has(t);
}

export function isDeprecatedType(type) {
  const t = String(type || "").toLowerCase();
  if (_cache?.questionTypes) {
    const hit = _cache.questionTypes.find((q) => (q.questionType || "").toLowerCase() === t);
    return hit?.deprecated === true;
  }
  return DEPRECATED_TYPES.has(t);
}

export function isMultiSelectType(type) {
  const t = String(type || "").toLowerCase();
  if (_cache?.questionTypes) {
    const hit = _cache.questionTypes.find((q) => (q.questionType || "").toLowerCase() === t);
    if (hit) return hit.multiSelect === true;
  }
  return MULTI_SELECT_TYPES_DEFAULT.has(t);
}

// Test-only / dev-tool helper to reset module state.
export function _resetCacheForTests() {
  _cache = null;
  _inFlight = null;
  _versionMismatch = false;
}
